import yaml from 'js-yaml';

export function generateSuggestions(spec) {
  const suggestions = [];
  
  try {
    const parsed = yaml.load(spec);
    
    // Missing contact info
    if (!parsed.info?.contact) {
      suggestions.push({
        message: 'Add contact information for API support',
        apply: (content) => addContactInfo(content)
      });
    }
    
    // Missing license
    if (!parsed.info?.license) {
      suggestions.push({
        message: 'Add license information',
        apply: (content) => addLicense(content)
      });
    }
    
    // Missing servers
    if (!parsed.servers || parsed.servers.length === 0) {
      suggestions.push({
        message: 'Add server URLs for API endpoints',
        apply: (content) => addServer(content)
      });
    }
    
    // Missing tags
    if (!parsed.tags || parsed.tags.length === 0) {
      const usedTags = getUsedTags(parsed);
      if (usedTags.length > 0) {
        suggestions.push({
          message: `Define tags: ${usedTags.join(', ')}`,
          apply: (content) => addTags(content, usedTags)
        });
      }
    }
    
    // Missing external docs
    if (!parsed.externalDocs) {
      suggestions.push({
        message: 'Add external documentation link',
        apply: (content) => addExternalDocs(content)
      });
    }
    
    // Operations without operationId
    const missingOpIds = checkMissingOperationIds(parsed);
    if (missingOpIds.length > 0) {
      suggestions.push({
        message: `Add operationId to ${missingOpIds.length} endpoint(s)`,
        apply: (content) => addOperationIds(content, parsed)
      });
    }
    
    // Operations without tags
    const untaggedOps = checkUntaggedOperations(parsed);
    if (untaggedOps.length > 0) {
      suggestions.push({
        message: `Add tags to ${untaggedOps.length} endpoint(s) for better organization`,
        apply: (content) => addDefaultTags(content, parsed)
      });
    }
    
    // Missing security schemes
    if (!parsed.components?.securitySchemes) {
      suggestions.push({
        message: 'Add security scheme (API Key, OAuth2, etc.)',
        apply: (content) => addSecurityScheme(content)
      });
    }
    
    // Schemas without descriptions
    const schemasWithoutDesc = checkSchemasWithoutDescriptions(parsed);
    if (schemasWithoutDesc.length > 0) {
      suggestions.push({
        message: `Add descriptions to ${schemasWithoutDesc.length} schema(s)`,
        apply: (content) => addSchemaDescriptions(content, schemasWithoutDesc)
      });
    }
    
    // Operations without examples
    if (checkMissingExamples(parsed)) {
      suggestions.push({
        message: 'Consider adding examples to request/response bodies',
        apply: null
      });
    }
    
    // Missing API version in URL
    if (parsed.servers && parsed.servers.length > 0) {
      const hasVersion = parsed.servers.some(s => s.url.match(/\/v\d+/));
      if (!hasVersion) {
        suggestions.push({
          message: 'Add version number to server URL (e.g., /v1)',
          apply: (content) => addVersionToServer(content)
        });
      }
    }
    
    // Missing response content types
    const missingContentTypes = checkMissingResponseContent(parsed);
    if (missingContentTypes > 0) {
      suggestions.push({
        message: `Add content types to ${missingContentTypes} response(s)`,
        apply: null
      });
    }
    
    // Operations without request body for POST/PUT/PATCH
    const missingRequestBodies = checkMissingRequestBodies(parsed);
    if (missingRequestBodies.length > 0) {
      suggestions.push({
        message: `Add request body to ${missingRequestBodies.length} POST/PUT/PATCH operation(s)`,
        apply: null
      });
    }
    
    // Missing error responses
    const missingErrorResponses = checkMissingErrorResponses(parsed);
    if (missingErrorResponses > 0) {
      suggestions.push({
        message: `Add error responses (4xx, 5xx) to ${missingErrorResponses} operation(s)`,
        apply: null
      });
    }
    
    // Schemas without examples
    const schemasWithoutExamples = checkSchemasWithoutExamples(parsed);
    if (schemasWithoutExamples.length > 0) {
      suggestions.push({
        message: `Add examples to ${schemasWithoutExamples.length} schema(s)`,
        apply: null
      });
    }
    
    // Missing deprecation warnings
    if (checkDeprecatedEndpoints(parsed)) {
      suggestions.push({
        message: 'Consider marking old API versions as deprecated',
        apply: null
      });
    }
    
    // Missing rate limiting info
    if (!checkRateLimitingDocs(parsed)) {
      suggestions.push({
        message: 'Document rate limiting in API description or headers',
        apply: (content) => addRateLimitingDocs(content)
      });
    }
    
    // Missing pagination for list endpoints
    const missingPagination = checkMissingPagination(parsed);
    if (missingPagination.length > 0) {
      suggestions.push({
        message: `Add pagination parameters to ${missingPagination.length} list endpoint(s)`,
        apply: null
      });
    }
    
    // Inconsistent naming conventions
    if (checkInconsistentNaming(parsed)) {
      suggestions.push({
        message: 'Use consistent naming (camelCase or snake_case) across schemas',
        apply: null
      });
    }
    
  } catch (e) {
    // Invalid YAML, no suggestions
  }
  
  return suggestions;
}

function getUsedTags(spec) {
  const tags = new Set();
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && operation.tags) {
        operation.tags.forEach(tag => tags.add(tag));
      }
    }
  }
  return Array.from(tags);
}

function checkMissingOperationIds(spec) {
  const missing = [];
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && !operation.operationId) {
        missing.push({ path, method });
      }
    }
  }
  return missing;
}

function checkUntaggedOperations(spec) {
  const untagged = [];
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && !operation.tags) {
        untagged.push({ path, method });
      }
    }
  }
  return untagged;
}

function checkSchemasWithoutDescriptions(spec) {
  const missing = [];
  if (spec.components?.schemas) {
    for (let name in spec.components.schemas) {
      if (!spec.components.schemas[name].description) {
        missing.push(name);
      }
    }
  }
  return missing;
}

function checkMissingExamples(spec) {
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object') {
        if (operation.requestBody?.content) {
          return true;
        }
        if (operation.responses) {
          for (let code in operation.responses) {
            if (operation.responses[code].content) {
              return true;
            }
          }
        }
      }
    }
  }
  return false;
}

function checkMissingResponseContent(spec) {
  let count = 0;
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && operation.responses) {
        for (let code in operation.responses) {
          if (code.startsWith('2') && !operation.responses[code].content) {
            count++;
          }
        }
      }
    }
  }
  return count;
}

function checkMissingRequestBodies(spec) {
  const missing = [];
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method of ['post', 'put', 'patch']) {
      const operation = paths[path][method];
      if (operation && typeof operation === 'object' && !operation.requestBody) {
        missing.push({ path, method });
      }
    }
  }
  return missing;
}

function checkMissingErrorResponses(spec) {
  let count = 0;
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && operation.responses) {
        const hasError = Object.keys(operation.responses).some(code => code.startsWith('4') || code.startsWith('5'));
        if (!hasError) count++;
      }
    }
  }
  return count;
}

function checkSchemasWithoutExamples(spec) {
  const missing = [];
  if (spec.components?.schemas) {
    for (let name in spec.components.schemas) {
      const schema = spec.components.schemas[name];
      if (!schema.example && !schema.examples) {
        missing.push(name);
      }
    }
  }
  return missing;
}

function checkDeprecatedEndpoints(spec) {
  const paths = spec.paths || {};
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && operation.deprecated) {
        return false; // Already using deprecation
      }
    }
  }
  return Object.keys(paths).length > 5; // Suggest if API has many endpoints
}

function checkRateLimitingDocs(spec) {
  const description = spec.info?.description || '';
  return description.toLowerCase().includes('rate limit') || description.toLowerCase().includes('throttle');
}

function checkMissingPagination(spec) {
  const missing = [];
  const paths = spec.paths || {};
  for (let path in paths) {
    const operation = paths[path].get;
    if (operation && typeof operation === 'object') {
      const pathLower = path.toLowerCase();
      if (pathLower.includes('list') || pathLower.endsWith('s') || operation.summary?.toLowerCase().includes('list')) {
        const hasPageParam = operation.parameters?.some(p => 
          p.name === 'page' || p.name === 'limit' || p.name === 'offset' || p.name === 'pageSize'
        );
        if (!hasPageParam) {
          missing.push(path);
        }
      }
    }
  }
  return missing;
}

function checkInconsistentNaming(spec) {
  if (!spec.components?.schemas) return false;
  
  const properties = [];
  for (let name in spec.components.schemas) {
    const schema = spec.components.schemas[name];
    if (schema.properties) {
      properties.push(...Object.keys(schema.properties));
    }
  }
  
  if (properties.length < 2) return false;
  
  const hasCamelCase = properties.some(p => /[a-z][A-Z]/.test(p));
  const hasSnakeCase = properties.some(p => p.includes('_'));
  
  return hasCamelCase && hasSnakeCase;
}

function addVersionToServer(content) {
  const lines = content.split('\n');
  const serverIndex = lines.findIndex(line => line.match(/^servers:/));
  
  if (serverIndex !== -1) {
    const urlIndex = lines.findIndex((line, i) => i > serverIndex && line.includes('url:'));
    if (urlIndex !== -1) {
      lines[urlIndex] = lines[urlIndex].replace(/(url:\s*https?:\/\/[^/]+)/, '$1/v1');
    }
  }
  
  return lines.join('\n');
}

function addRateLimitingDocs(content) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.match(/^info:/));
  
  if (infoIndex !== -1) {
    const descIndex = lines.findIndex((line, i) => i > infoIndex && line.includes('description:'));
    if (descIndex !== -1) {
      lines[descIndex] = lines[descIndex] + ' Rate limiting: 1000 requests per hour per API key.';
    } else {
      let insertIndex = infoIndex + 1;
      while (insertIndex < lines.length && lines[insertIndex].match(/^  \w/)) {
        insertIndex++;
      }
      lines.splice(insertIndex, 0, '  description: Rate limiting: 1000 requests per hour per API key.');
    }
  }
  
  return lines.join('\n');
}

function addContactInfo(content) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.match(/^info:/));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex + 1;
    while (insertIndex < lines.length && lines[insertIndex].match(/^  \w/)) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, 
      '  contact:',
      '    name: API Support',
      '    email: support@example.com',
      '    url: https://example.com/support'
    );
  }
  
  return lines.join('\n');
}

function addLicense(content) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.match(/^info:/));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex + 1;
    while (insertIndex < lines.length && lines[insertIndex].match(/^  \w/)) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, 
      '  license:',
      '    name: MIT',
      '    url: https://opensource.org/licenses/MIT'
    );
  }
  
  return lines.join('\n');
}

function addServer(content) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.match(/^info:/));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex;
    while (insertIndex < lines.length && lines[insertIndex].match(/^(info:|  )/)) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, 
      'servers:',
      '  - url: https://api.example.com/v1',
      '    description: Production server'
    );
  }
  
  return lines.join('\n');
}

function addTags(content, tags) {
  const lines = content.split('\n');
  const pathsIndex = lines.findIndex(line => line.match(/^paths:/));
  
  if (pathsIndex !== -1) {
    const tagLines = ['tags:'];
    tags.forEach(tag => {
      tagLines.push(`  - name: ${tag}`);
      tagLines.push(`    description: ${tag} operations`);
    });
    lines.splice(pathsIndex, 0, ...tagLines);
  }
  
  return lines.join('\n');
}

function addExternalDocs(content) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.match(/^info:/));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex;
    while (insertIndex < lines.length && lines[insertIndex].match(/^(info:|  )/)) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, 
      'externalDocs:',
      '  description: Find more info here',
      '  url: https://docs.example.com'
    );
  }
  
  return lines.join('\n');
}

function addOperationIds(content, spec) {
  const lines = content.split('\n');
  const paths = spec.paths || {};
  
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && !operation.operationId) {
        const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
        if (pathIndex !== -1) {
          const methodIndex = lines.findIndex((line, i) => i > pathIndex && line.match(new RegExp(`^    ${method}:`)));
          if (methodIndex !== -1) {
            const opId = `${method}${path.replace(/[^a-zA-Z0-9]/g, '')}`;
            lines.splice(methodIndex + 1, 0, `      operationId: ${opId}`);
          }
        }
      }
    }
  }
  
  return lines.join('\n');
}

function addDefaultTags(content, spec) {
  const lines = content.split('\n');
  const paths = spec.paths || {};
  
  for (let path in paths) {
    for (let method in paths[path]) {
      const operation = paths[path][method];
      if (typeof operation === 'object' && !operation.tags) {
        const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
        if (pathIndex !== -1) {
          const methodIndex = lines.findIndex((line, i) => i > pathIndex && line.match(new RegExp(`^    ${method}:`)));
          if (methodIndex !== -1) {
            const tag = path.split('/')[1] || 'default';
            lines.splice(methodIndex + 1, 0, `      tags:`, `        - ${tag}`);
          }
        }
      }
    }
  }
  
  return lines.join('\n');
}

function addSecurityScheme(content) {
  const lines = content.split('\n');
  let componentsIndex = lines.findIndex(line => line.match(/^components:/));
  
  if (componentsIndex === -1) {
    lines.push('components:');
    componentsIndex = lines.length - 1;
  }
  
  lines.splice(componentsIndex + 1, 0,
    '  securitySchemes:',
    '    ApiKeyAuth:',
    '      type: apiKey',
    '      in: header',
    '      name: X-API-Key'
  );
  
  return lines.join('\n');
}

function addSchemaDescriptions(content, schemas) {
  const lines = content.split('\n');
  
  schemas.forEach(schemaName => {
    const schemaIndex = lines.findIndex(line => line.includes(`${schemaName}:`));
    if (schemaIndex !== -1) {
      lines.splice(schemaIndex + 1, 0, `      description: ${schemaName} schema`);
    }
  });
  
  return lines.join('\n');
}
